import Joi from 'joi';

import { ContactSourceEnum } from '../../../../enums/contact-source.enum';
import { ContactTitleEnum } from '../../../../enums/contact-title.enum';
import { phoneSchema } from '../../../../validators/schemas/phone.schema';

/**
 * Shared nullable write fields for tenant-owned contacts (BaseContactDto surface).
 */
export const tenantContactWriteFields = () => ({
  email: Joi.string()
    .email({ tlds: { allow: false } })
    .allow(null)
    .messages({
      'string.base': 'validations|contact-email|must-be-string',
      'string.email': 'validations|contact-email|invalid',
    }),

  address1: Joi.string().allow(null).messages({
    'string.base': 'validations|contact-address1|must-be-string',
  }),

  address2: Joi.string().allow(null).messages({
    'string.base': 'validations|contact-address2|must-be-string',
  }),

  phoneLandline: phoneSchema().allow(null).messages({
    'string.pattern.base': 'validations|contact-phone-landline|invalid',
    'string.base': 'validations|contact-phone-landline|must-be-string',
  }),

  phoneMobile: phoneSchema().allow(null).messages({
    'string.pattern.base': 'validations|contact-phone-mobile|invalid',
    'string.base': 'validations|contact-phone-mobile|must-be-string',
  }),

  phoneFax: phoneSchema().allow(null).messages({
    'string.pattern.base': 'validations|contact-phone-fax|invalid',
    'string.base': 'validations|contact-phone-fax|must-be-string',
  }),

  city: Joi.string().allow(null).messages({
    'string.base': 'validations|contact-city|must-be-string',
  }),

  postCode: Joi.string().allow(null).messages({
    'string.base': 'validations|contact-post-code|must-be-string',
  }),

  poBox: Joi.string().allow(null).messages({
    'string.base': 'validations|contact-po-box|must-be-string',
  }),

  county: Joi.string().allow(null).messages({
    'string.base': 'validations|contact-county|must-be-string',
  }),

  state: Joi.string().allow(null).messages({
    'string.base': 'validations|contact-state|must-be-string',
  }),

  countryId: Joi.number().allow(null).messages({
    'number.base': 'validations|contact-country-id|must-be-number',
  }),

  source: Joi.string()
    .valid(...Object.values(ContactSourceEnum))
    .allow(null)
    .messages({
      'any.only': 'validations|contact-source|invalid-value',
    }),

  companyRegNumber: Joi.string().allow(null).messages({
    'string.base': 'validations|contact-company-reg-number|must-be-string',
  }),

  companyTaxNumber: Joi.string().allow(null).messages({
    'string.base': 'validations|contact-company-tax-number|must-be-string',
  }),

  companyVatNumber: Joi.string().allow(null).messages({
    'string.base': 'validations|contact-company-vat-number|must-be-string',
  }),

  firstName: Joi.string().allow(null).messages({
    'string.base': 'validations|contact-first-name|must-be-string',
  }),

  middleNames: Joi.string().allow(null).messages({
    'string.base': 'validations|contact-middle-names|must-be-string',
  }),

  lastName: Joi.string().allow(null).messages({
    'string.base': 'validations|contact-last-name|must-be-string',
  }),

  title: Joi.string()
    .valid(...Object.values(ContactTitleEnum))
    .allow(null)
    .messages({
      'any.only': 'validations|contact-title|invalid-value',
    }),

  dateOfBirth: Joi.date().allow(null).messages({
    'date.base': 'validations|contact-date-of-birth|must-be-a-valid-date',
  }),

  preferredContactFrom: Joi.date().allow(null).messages({
    'date.base':
      'validations|contact-preferred-contact-from|must-be-a-valid-date',
  }),

  preferredContactTo: Joi.date().allow(null).messages({
    'date.base':
      'validations|contact-preferred-contact-to|must-be-a-valid-date',
  }),

  website: Joi.string().allow(null).messages({
    'string.base': 'validations|contact-website|must-be-string',
  }),

  facebook: Joi.string().allow(null).messages({
    'string.base': 'validations|contact-facebook|must-be-string',
  }),

  instagram: Joi.string().allow(null).messages({
    'string.base': 'validations|contact-instagram|must-be-string',
  }),

  twitter_x: Joi.string().allow(null).messages({
    'string.base': 'validations|contact-twitter_x|must-be-string',
  }),

  slack: Joi.string().allow(null).messages({
    'string.base': 'validations|contact-slack|must-be-string',
  }),

  whatsApp: Joi.string().allow(null).messages({
    'string.base': 'validations|contact-whatsapp|must-be-string',
  }),

  youtube: Joi.string().allow(null).messages({
    'string.base': 'validations|contact-youtube|must-be-string',
  }),

  linkedIn: Joi.string().allow(null).messages({
    'string.base': 'validations|contact-linkedin|must-be-string',
  }),
});

/**
 * companyName required for agent/supplier contacts; nullable-optional elsewhere.
 */
export const tenantContactCompanyNameRequired = () => ({
  companyName: Joi.alternatives().conditional(Joi.any().valid(null), {
    then: Joi.invalid(null).messages({
      'any.invalid': 'validations|contact-company-name|cannot-be-null',
    }),
    otherwise: Joi.string().required().messages({
      'any.required': 'validations|contact-company-name|required',
      'string.base': 'validations|contact-company-name|must-be-string',
      'string.empty': 'validations|contact-company-name|must-not-be-empty',
    }),
  }),
});

/**
 * companyName optional on location contacts.
 */
export const tenantContactCompanyNameOptional = () => ({
  companyName: Joi.string().allow(null).messages({
    'string.base': 'validations|contact-company-name|must-be-string',
  }),
});

/**
 * Nested contact id must not be sent on create.
 */
export const tenantContactCreateIdRule = () => ({
  id: Joi.forbidden().messages({
    'any.unknown': 'validations|contact-id|must-not-be-sent',
  }),
});

/**
 * Nested contact id required as a number on update.
 */
export const tenantContactUpdateIdRule = () => ({
  id: Joi.alternatives().conditional(Joi.any().valid(null), {
    then: Joi.invalid(null).messages({
      'any.invalid': 'validations|contact-id|cannot-be-null',
    }),
    otherwise: Joi.number().required().messages({
      'any.required': 'validations|contact-id|is-required',
      'number.base': 'validations|contact-id|must-be-number',
    }),
  }),
});
